import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  RadioGroup,
  Radio,
  TextField,
  InputAdornment,
  ListSubheader,
  Divider,
  IconButton
} from '@mui/material';
import DateRangePickerWrapper from "components/DateRangePickerWrapper";
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import { format, parse, isValid, addDays } from 'date-fns';
import { DateRange } from "mui-daterange-picker";
import useApi from 'hooks/useApi';
import { downloadCSV, downloadXLSX, generateFilename, formatTransactionForExport } from 'utils/exportUtils';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  teamOptions: any[];
  selectedTeam: string;
  dateRange: any;
  clientOptions: any[];
  selectedClient: string;
  // Transaction filters from main page
  currentFilters: {
    search: string;
    reportingStatus: string;
    status: string;
    dateType: string;
  };
}

interface FieldOption {
  key: string;
  label: string;
  category: 'basic' | 'workflow' | 'metadata';
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  teamOptions,
  selectedTeam,
  dateRange,
  clientOptions,
  selectedClient,
  currentFilters
}) => {
  const [exportTeam, setExportTeam] = useState(selectedTeam || '');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [workflowFields, setWorkflowFields] = useState<string[]>([]);
  const [fileFormat, setFileFormat] = useState<'csv' | 'xlsx'>('csv');
  const [processing, setProcessing] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [downloadReady, setDownloadReady] = useState(false);
  const [teamFieldOptions, setTeamFieldOptions] = useState<any[]>([]);
  const [workflowSearchText, setWorkflowSearchText] = useState<string>('');
  const [loadingWorkflowFields, setLoadingWorkflowFields] = useState<boolean>(false);
  console.log("date range",dateRange);
  // Filter states
  const [exportDateRange, setExportDateRange] = useState<DateRange>(() => {
  const currentYear = new Date().getFullYear();
  const startDate = new Date(currentYear, 0, 1);
  const endDate = addDays(new Date(currentYear, 11, 31), 1);

  return {
    startDate,
    endDate
  };
});
  const [exportReportingStatus, setExportReportingStatus] = useState(currentFilters.reportingStatus);
  const [exportStatus, setExportStatus] = useState(currentFilters.status);
  const [exportDateType, setExportDateType] = useState(currentFilters.dateType);
  
  // Date input states
  const [openDateRange, setOpenDateRange] = useState(false);
const [inputValues, setInputValues] = useState<DateRange>(() => {
  const currentYear = new Date().getFullYear();
  const startDate = new Date(currentYear, 0, 1);
  const endDate = addDays(new Date(currentYear, 11, 31), 1);

  return {
    startDate,
    endDate
  };
});
  
  // Filter options states
  const [nodeStatusOptions, setNodeStatusOptions] = useState<any[]>([]);
  const [stepStatusOptions, setStepStatusOptions] = useState<any[]>([]);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [teamDetails, setTeamDetails] = useState<any>(null);
  const [downloadCountdown, setDownloadCountdown] = useState<number | null>(null);
  
  const { transactionOrderWithHistory, getFlowByTeamUid, getNodeStatus, getTeamDetailsByUid, showToast } = useApi();

  // Available basic fields for export
  const basicFieldOptions: FieldOption[] = [
    { key: 'transactionId', label: 'Transaction ID', category: 'basic' },
    { key: 'clientName', label: 'Client Name', category: 'basic' },
    { key: 'teamName', label: 'Process Name', category: 'basic' },
    { key: 'createdDate', label: 'Created Date', category: 'basic' },
    { key: 'status', label: 'Current Status', category: 'basic' },
  ];

  useEffect(() => {
    if (open) {
      setExportTeam(selectedTeam || '');
      setSelectedFields(['transactionId', 'clientName', 'teamName', 'createdDate', 'status']);
      setDownloadReady(false);
      setExportData([]);
    }
  }, [open, selectedTeam]);

  // Fetch workflow fields when team changes. Gated on `open` — this dialog is
  // mounted unconditionally by the parent page (only its visibility toggles),
  // so without this guard `exportTeam` being seeded from the `selectedTeam`
  // prop on mount fired these fetches immediately, duplicating the same
  // team/flow/node-status calls the transactions page had already made.
  useEffect(() => {
    if (!open) return;
    if (exportTeam && exportTeam !== 'all') {
      setWorkflowFields([]); // Reset workflow fields selection
      setWorkflowSearchText(''); // Reset search text
      fetchTeamFields(exportTeam);
      fetchFilterOptions(exportTeam);
    } else {
      setTeamFieldOptions([]);
      setWorkflowFields([]);
      setWorkflowSearchText('');
      setNodeStatusOptions([]);
      setStepStatusOptions([]);
      setTeamDetails(null);
    }
  }, [exportTeam, open]);

  const fetchTeamFields = async (teamUid: string) => {
    try {
      setLoadingWorkflowFields(true);
      const flowResp = await getFlowByTeamUid(teamUid);
      if (flowResp?.data?.steps) {
        setTeamFieldOptions(flowResp.data.steps);
      }
    } catch (error) {
      console.error('Error fetching team fields:', error);
    } finally {
      setLoadingWorkflowFields(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async (teamUid: string) => {
    try {
      setLoadingFilters(true);
      
      // Fetch team details for dynamic date labels
      const teamDetailsResp = await getTeamDetailsByUid(teamUid);
      if (teamDetailsResp?.success && teamDetailsResp.data?.settings) {
        setTeamDetails(teamDetailsResp.data.settings);
      }
      
      // Fetch node status options (reporting status)
      const nodeStatusResp = await getNodeStatus(teamUid);
      if (nodeStatusResp?.success && Array.isArray(nodeStatusResp.data)) {
        setNodeStatusOptions(nodeStatusResp.data);
      }
      
      // Fetch step status options from team fields
      const flowResp = await getFlowByTeamUid(teamUid);
      if (flowResp?.data?.steps && Array.isArray(flowResp.data.steps)) {
        setStepStatusOptions(flowResp.data.steps);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (range: any) => {
    console.log("range data", range);

    const formatDate = (date: any) => {
      // If it's already a string in dd/MM/yyyy format, return as-is
      if (typeof date === 'string') {
        return date;
      }

      // If it's a Date object, format it
      if (date instanceof Date && isValid(date)) {
        return format(date, 'dd/MM/yyyy');
      }

      return date;
    };

    const formattedRange = {
      startDate: formatDate(range.startDate),
      endDate: formatDate(range.endDate)
    };

    setInputValues(formattedRange);
    setExportDateRange(range);
  };

  // Handle date input change
  const handleInputChange = (key: "startDate" | "endDate", value: string) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
    const parsed = parse(value, "dd/MM/yyyy", new Date());
    if (isValid(parsed)) {
      setExportDateRange((prev: any) => ({ ...prev, [key]: parsed }));
    }
  };

  // Get all available fields (basic + workflow)
  const getAllFieldOptions = (): FieldOption[] => {
    const workflowFieldOptions: FieldOption[] = [];
    
    if (Array.isArray(teamFieldOptions)) {
      teamFieldOptions.forEach(step => {
        const allFields = step.component?.fields || [];
        allFields.forEach((field: any) => {
          workflowFieldOptions.push({
            key: field.uid || field._id || field.id,
            label: field.question || field.label || field.name,
            category: 'workflow'
          });
        });
      });
    }
    
    return [...basicFieldOptions, ...workflowFieldOptions];
  };

  const fieldOptions = getAllFieldOptions();

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldKey)
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleSelectAllFields = (category?: string) => {
    if (category) {
      const categoryFields = fieldOptions
        .filter(field => field.category === category)
        .map(field => field.key);
      
      const otherFields = selectedFields.filter(fieldKey => 
        !fieldOptions.find(f => f.key === fieldKey && f.category === category)
      );
      
      setSelectedFields([...otherFields, ...categoryFields]);
    } else {
      setSelectedFields(fieldOptions.map(field => field.key));
    }
  };

  const handleDeselectAllFields = (category?: string) => {
    if (category) {
      const categoryFields = fieldOptions
        .filter(field => field.category === category)
        .map(field => field.key);
      
      setSelectedFields(prev => 
        prev.filter(fieldKey => !categoryFields.includes(fieldKey))
      );
    } else {
      setSelectedFields([]);
    }
  };

  const processExportData = async () => {
    if (!exportTeam || selectedFields.length === 0) {
      showToast('Please select a process and at least one field to export.', 'error');
      return;
    }

    setProcessing(true);

    try {
      // Fetch all transactions using the export filters
      const payload = {
        page: 1,
        size: 10000, // Large number to get all transactions
        search: currentFilters.search,
        from: exportDateRange.startDate ? format(exportDateRange.startDate, 'dd-MM-yyyy') : '',
        to: exportDateRange.endDate ? format(exportDateRange.endDate, 'dd-MM-yyyy') : '',
        eventTypes: [],
        eventStatus: [],
        clients: [selectedClient],
        teams: [exportTeam],
        users: [],
        keyword: '',
        reportingStatus: exportReportingStatus,
        dateType: exportDateType,
        status: exportStatus
      };

      const response = await transactionOrderWithHistory(payload);
      
      console.log('Export API Response:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);
      
      // Check multiple possible response structures
      let rawData = null;
      if (response?.success) {
        // Try different possible data structures based on main transaction page pattern
        rawData = response.data?.data || response.data?.rows || response.data || [];
        
        console.log('Raw transaction data:', rawData);
        console.log('Raw data type:', typeof rawData);
        console.log('Is array:', Array.isArray(rawData));
        console.log('Array length:', Array.isArray(rawData) ? rawData.length : 'Not an array');
        
        if (Array.isArray(rawData) && rawData.length > 0) {
          console.log('Sample transaction:', rawData[0]);
          
          // Transform data based on selected fields
          const processedData = rawData.map((txn: any) => 
            formatTransactionForExport(txn, selectedFields, workflowFields, teamFieldOptions)
          );

          console.log('Processed export data:', processedData);
          console.log('First processed item:', processedData[0]);
          
          // Check if processedData has content
          if (processedData.length > 0 && Object.keys(processedData[0]).length > 0) {
            setExportData(processedData);
            setDownloadReady(true);
            
            showToast(`Export processed successfully! ${processedData.length} transactions ready.`, 'success');
            
            // Add a small delay to ensure state is updated, then start countdown
            setTimeout(() => {
              setDownloadCountdown(3);
              
              const countdownInterval = setInterval(() => {
                setDownloadCountdown(prev => {
                  if (prev === null) return null;
                  const newCount = prev - 1;
                  if (newCount > 0) {
                    return newCount;
                  } else {
                    clearInterval(countdownInterval);
                    // Call download with the processedData directly to avoid state timing issues
                    handleDownloadWithData(processedData);
                    return null;
                  }
                });
              }, 1000);
            }, 100); // 100ms delay to ensure state updates
          } else {
            console.error('Processed data is empty or has no fields');
            showToast('Export data processing resulted in empty content. Please check your field selection.', 'error');
          }
        } else if (Array.isArray(rawData) && rawData.length === 0) {
          showToast('No transaction data found for the selected filters and date range.', 'info');
        } else {
          console.error('Unexpected data structure:', rawData);
          showToast(`Transaction data is not in expected format. Data type: ${typeof rawData}, Is array: ${Array.isArray(rawData)}`, 'error');
        }
      } else {
        console.error('API call failed:', response);
        showToast(`Failed to fetch transaction data: ${response?.message || response?.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Export processing error:', error);
      showToast('An error occurred while processing the export. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    handleDownloadWithData(exportData);
  };

  const handleDownloadWithData = (dataToDownload: any[]) => {
    console.log('handleDownloadWithData called with data:', dataToDownload);
    console.log('Data length:', dataToDownload.length);
    console.log('First item keys:', dataToDownload.length > 0 ? Object.keys(dataToDownload[0]) : 'No data');
    
    if (!dataToDownload || dataToDownload.length === 0) {
      showToast('No data to export. Please process the export first.', 'error');
      return;
    }
    
    // Check if export data has any content
    const hasContent = dataToDownload.some(item => Object.keys(item).length > 0);
    if (!hasContent) {
      showToast('Export data is empty. Please check your field selection and try again.', 'error');
      return;
    }
    
    const teamName = teamOptions.find(t => t.uid === exportTeam)?.name || 'Unknown';
    const fileName = generateFilename(`transactions_${teamName}`, fileFormat);
    
    try {
      if (fileFormat === 'csv') {
        downloadCSV(dataToDownload, fileName);
      } else {
        downloadXLSX(dataToDownload, fileName, 'Transactions');
      }
      
      showToast(`${fileFormat.toUpperCase()} file downloaded successfully!`, 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download the file. Please try again.', 'error');
    }
  };

  // Get all workflow field IDs
  const getAllWorkflowFieldIds = () => {
    const allWorkflowFields: string[] = [];
    teamFieldOptions.forEach(step => {
      const allFields = step.component?.fields || [];
      allFields.forEach((field: any) => {
        const fieldId = field.uid || field._id || field.id;
        if (fieldId) allWorkflowFields.push(fieldId);
      });
    });
    return allWorkflowFields;
  };

  // Handle select all workflow fields
  const handleSelectAllWorkflowFields = () => {
    const allWorkflowFieldIds = getAllWorkflowFieldIds();
    setWorkflowFields(allWorkflowFieldIds);
    
    // Also update selectedFields to include workflow fields
    const basicFields = selectedFields.filter(field => 
      basicFieldOptions.some(bf => bf.key === field)
    );
    setSelectedFields([...basicFields, ...allWorkflowFieldIds]);
  };

  // Handle deselect all workflow fields
  const handleDeselectAllWorkflowFields = () => {
    setWorkflowFields([]);
    
    // Remove workflow fields from selectedFields
    const basicFields = selectedFields.filter(field => 
      basicFieldOptions.some(bf => bf.key === field)
    );
    setSelectedFields(basicFields);
  };

  // Filter workflow fields based on search text
  const getFilteredWorkflowFields = () => {
    if (!workflowSearchText) return teamFieldOptions;
    
    return teamFieldOptions.map(step => {
      const filteredFields = (step.component?.fields || []).filter((field: any) => {
        const searchableText = (field.question || field.label || field.name || '').toLowerCase();
        return searchableText.includes(workflowSearchText.toLowerCase());
      });
      
      return {
        ...step,
        component: {
          ...step.component,
          fields: filteredFields
        }
      };
    }).filter(step => step.component?.fields?.length > 0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Export Transactions
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Process Selection */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
            Select Process
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={exportTeam}
              onChange={(e) => setExportTeam(e.target.value)}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                }
              }}
            >
              <MenuItem value="">
                <em>Choose a process</em>
              </MenuItem>
              {teamOptions
                .filter(team => team.uid !== 'all')
                .map((team) => (
                  <MenuItem key={team.uid} value={team.uid}>
                    {team.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        {/* File Format Selection */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
            Export Format
          </Typography>
          <RadioGroup
            row
            value={fileFormat}
            onChange={(e) => setFileFormat(e.target.value as 'csv' | 'xlsx')}
            sx={{ gap: 2 }}
          >
            <FormControlLabel 
              value="csv" 
              control={<Radio size="small" />} 
              label={
                <Typography variant="body2" fontWeight={500}>
                  CSV
                </Typography>
              }
            />
            <FormControlLabel 
              value="xlsx" 
              control={<Radio size="small" />} 
              label={
                <Typography variant="body2" fontWeight={500}>
                  XLSX
                </Typography>
              }
            />
          </RadioGroup>
        </Box>

        {/* Export Filters */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
            Filters
          </Typography>
          
          {loadingFilters ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1.5,
                bgcolor: 'grey.50'
              }}
            >
              <CircularProgress size={20} sx={{ mr: 1.5 }} />
              <Typography variant="body2" color="text.secondary">
                Loading filters...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Date Type and Date Range in a row */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                  <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                    <InputLabel id="export-date-type-label" sx={{ fontSize: '0.875rem' }}>Date Type</InputLabel>
                    <Select
                      labelId="export-date-type-label"
                      value={exportDateType}
                      label="Date Type"
                      onChange={(e) => setExportDateType(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    >
                      <MenuItem value="updatedDate">Updated Date</MenuItem>
                      <MenuItem value="createdDate">Created Date</MenuItem>
                      <MenuItem value="deliveryDate">{teamDetails?.dueDateLabel || 'Delivery Date'}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="From"
                    value={inputValues?.startDate ? (typeof inputValues.startDate === 'string' ? inputValues.startDate : format(new Date(inputValues.startDate), 'dd/MM/yyyy')) : ""}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    size="small"
                    placeholder="DD/MM/YYYY"
                    inputProps={{ readOnly: true }}
                    sx={{ 
                      minWidth: 110, 
                      flex: 1,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                  <TextField
                    label="To"
                    value={inputValues.endDate ? (typeof inputValues.endDate === 'string' ? inputValues.endDate : format(new Date(inputValues.endDate), 'dd/MM/yyyy')) : ""}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    size="small"
                    placeholder="DD/MM/YYYY"
                    inputProps={{ readOnly: true }}
                    sx={{ 
                      minWidth: 110, 
                      flex: 1,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                  <IconButton
                    onClick={() => setOpenDateRange(!openDateRange)}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      },
                      borderRadius: 1.5,
                      width: 38,
                      height: 38
                    }}
                  >
                    <DateRangeIcon fontSize="small" />
                  </IconButton>
                </Box>

                {openDateRange && (
                  <Box sx={{ position: 'fixed', zIndex: 9999, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                    <DateRangePickerWrapper
                      open={openDateRange}
                      toggle={() => setOpenDateRange(!openDateRange)}
                      onChange={handleDateRangeChange}
                      initialDateRange={exportDateRange}
                    />
                  </Box>
                )}

                {/* Status filters in a row */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {nodeStatusOptions.length > 0 && (
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Reporting Status</InputLabel>
                      <Select
                        label="Reporting Status"
                        value={exportReportingStatus}
                        onChange={(e) => setExportReportingStatus(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: '66vh',
                              width: 300,
                            },
                          },
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {nodeStatusOptions.map((status: any, idx: any) => (
                          <MenuItem key={status.uid + idx} value={status._id}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {stepStatusOptions.length > 0 && (
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Step Status</InputLabel>
                      <Select
                        label="Step Status"
                        value={exportStatus}
                        onChange={(e) => setExportStatus(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: '66vh',
                              width: 300,
                            },
                          },
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {stepStatusOptions.map((step: any, idx: any) => (
                          <MenuItem key={step.title + idx} value={`${step.title} Completed`}>
                            {`${step.title} Completed`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Field Selection */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
            Fields to Export
          </Typography>
          
          {/* Basic Fields */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>
                Basic Fields
              </Typography>
              <Box>
                <Button 
                  size="small" 
                  variant="text"
                  onClick={() => handleSelectAllFields('basic')}
                  sx={{ mr: 1, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  Select All
                </Button>
                <Button 
                  size="small" 
                  variant="text"
                  onClick={() => handleDeselectAllFields('basic')}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}>
              <FormGroup row>
                {basicFieldOptions.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        onChange={() => handleFieldToggle(field.key)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {field.label}
                      </Typography>
                    }
                    sx={{ minWidth: '180px', mb: 0.5 }}
                  />
                ))}
              </FormGroup>
            </Box>
          </Box>

          {/* Workflow Fields Multi-Select */}
          {(loadingWorkflowFields || teamFieldOptions.length > 0) && (
            <Box mb={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>
                  Workflow Fields
                </Typography>
                {!loadingWorkflowFields && (
                  <Box>
                    <Button 
                      size="small" 
                      variant="text"
                      onClick={handleSelectAllWorkflowFields}
                      sx={{ mr: 1, fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      variant="text"
                      onClick={handleDeselectAllWorkflowFields}
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Clear
                    </Button>
                  </Box>
                )}
              </Box>
              
              {loadingWorkflowFields ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1.5,
                    bgcolor: 'grey.50'
                  }}
                >
                  <CircularProgress size={20} sx={{ mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading workflow fields...
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={workflowFields}
                    onChange={(e) => {
                      const selectedValues = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                      setWorkflowFields(selectedValues);
                      
                      // Also update selectedFields to include workflow fields
                      const basicFields = selectedFields.filter(field => 
                        basicFieldOptions.some(bf => bf.key === field)
                      );
                      setSelectedFields([...basicFields, ...selectedValues]);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Select workflow fields...';
                      
                      const names = selected.map((value: string) => {
                        // Find the field in teamFieldOptions
                        for (const step of teamFieldOptions) {
                          const field = step.component?.fields?.find((f: any) => 
                            (f.uid || f._id || f.id) === value
                          );
                          if (field) {
                            return field.question || field.label || field.name;
                          }
                        }
                        return value;
                      });
                      
                      return selected.length > 2 
                        ? `${names.slice(0, 2).join(', ')} +${selected.length - 2} more`
                        : names.join(', ');
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: '66vh',
                          width: 400,
                        },
                      },
                    }}
                  >
                    {/* Search Box */}
                    <ListSubheader sx={{ bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search fields..."
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        value={workflowSearchText}
                        onChange={(e) => setWorkflowSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") {
                            e.stopPropagation();
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          }
                        }}
                      />
                    </ListSubheader>
                    
                    <Divider />
                    
                    {getFilteredWorkflowFields().map((step, stepIdx) => {
                      const allFields = step.component?.fields || [];
                      if (allFields.length === 0) return null;
                      
                      return [
                        <MenuItem key={`${step.title}-${stepIdx}`} disabled divider sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {step.title}
                        </MenuItem>,
                        ...allFields.map((field: any) => (
                          <MenuItem
                            key={field.uid || field._id || field.id}
                            value={field.uid || field._id || field.id}
                            sx={{ pl: 3 }}
                          >
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {field.question || field.label || field.name}
                            </Typography>
                          </MenuItem>
                        ))
                      ];
                    })}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </Box>

        {/* Export Summary */}
        {exportTeam && selectedFields.length > 0 && (
          <Box 
            sx={{ 
              p: 2.5, 
              bgcolor: 'success.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'success.200',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: 'success.main', mr: 1, fontSize: '1.25rem' }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: 'success.main' }}>
                Export Summary
              </Typography>
            </Box>
            
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {/* Process and Format Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Process
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mt: 0.25 }}>
                    {teamOptions.find(t => t.uid === exportTeam)?.name || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    File Format
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mt: 0.25 }}>
                    {fileFormat.toUpperCase()}
                  </Typography>
                </Box>
              </Box>

              {/* Fields Selection */}
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Selected Fields
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mt: 0.25 }}>
                  {selectedFields.length} total fields ({selectedFields.filter(f => basicFieldOptions.some(bf => bf.key === f)).length} basic, {workflowFields.length} workflow)
                </Typography>
              </Box>

              {/* Date Configuration */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Sort By
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mt: 0.25 }}>
                    {exportDateType === 'updatedDate' ? 'Updated Date' : exportDateType === 'createdDate' ? 'Created Date' : (teamDetails?.dueDateLabel || 'Delivery Date')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Date Range
                  </Typography>
                            <Typography
            variant="body2"
            sx={{ color: 'text.primary', fontWeight: 500, mt: 0.25 }}
          >
            {exportDateRange.startDate ? format(new Date(exportDateRange.startDate), 'dd/MM/yyyy') : ''} - 
            {exportDateRange.endDate ? format(new Date(exportDateRange.endDate), 'dd/MM/yyyy') : ''}
          </Typography>
                </Box>
              </Box>

              {/* Active Filters */}
              {(exportReportingStatus || exportStatus) && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Active Filters
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {exportReportingStatus && (
                      <Box 
                        sx={{ 
                          px: 1.5, 
                          py: 0.5, 
                          bgcolor: 'info.100', 
                          borderRadius: 1, 
                          border: '1px solid', 
                          borderColor: 'info.300'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 600 }}>
                          {nodeStatusOptions.find(s => s._id === exportReportingStatus)?.name || 'Reporting Status'}
                        </Typography>
                      </Box>
                    )}
                    {exportStatus && (
                      <Box 
                        sx={{ 
                          px: 1.5, 
                          py: 0.5, 
                          bgcolor: 'warning.100', 
                          borderRadius: 1, 
                          border: '1px solid', 
                          borderColor: 'warning.300'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                          {exportStatus}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        
        {!downloadReady ? (
          <Button
            variant="contained"
            onClick={processExportData}
            disabled={processing || !exportTeam || selectedFields.length === 0 || loadingWorkflowFields || loadingFilters}
            startIcon={processing ? <CircularProgress size={16} /> : <GetAppIcon />}
          >
            {processing ? 'Processing...' : (loadingWorkflowFields || loadingFilters) ? 'Loading...' : 'Process Export'}
          </Button>
        ) : (
          <>
            {downloadCountdown !== null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="success.main">
                  Auto downloading in {downloadCountdown} seconds...
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setDownloadCountdown(null);
                  }}
                >
                  Cancel Auto Download
                </Button>
              </Box>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleDownload}
                  startIcon={<CloudDownloadIcon />}
                >
                  Download {fileFormat.toUpperCase()}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDownloadReady(false);
                    setExportData([]);
                    setDownloadCountdown(null);
                  }}
                >
                  Process New Export
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
